import { useEffect, useRef } from 'react';

/**
 * Hook to run logic on component mount, replicating Ember route lifecycle methods
 *
 * Use this hook when migrating from Ember routes to React components and you need to
 * replicate logic that was previously in Ember route lifecycle methods:
 * - `beforeModel()` - Setup logic before data loading
 * - `model()` - Data fetching and preparation
 * - `afterModel()` - Post-processing after data is loaded
 * - `activate()` - Logic that runs when route becomes active
 *
 * The callback can optionally return a cleanup function that will be called on unmount.
 * For async callbacks (returning Promise), cleanup is not supported.
 */
export const useOnMount = (
  callback: (() => void) | (() => () => void) | (() => Promise<void>)
): void => {
  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;

    const fn = callback();

    return () => {
      // Only call cleanup if fn is actually a function (not a Promise)
      if (typeof fn === 'function') {
        fn();
      }
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- To replicate Ember activate/setup lifecycle
  }, []);
};
